import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Text } from "grommet";

const DEFAULT_TAGS = [
  "firstName",
  "lastName",
  "checkinDate",
  "checkinTime",
  "checkoutDate",
  "checkoutTime",
];

const TAG_LABELS = {
  chatLink: "Chat link",
  firstName: "First Name",
  lastName: "Last Name",
  hotel: "Hotel",
  hotelPhone: "Hotel Phone",
  checkinDate: "Check-in Date",
  checkinTime: "Check-in Time",
  checkoutDate: "Check-out Date",
  checkoutTime: "Check-out Time",
  roomsAndCodes: "Rooms & Codes",
  RoomsAndCodes: "Rooms & Codes",
  downloadAppButton: "Download App Button",
  mobileAppGuide: "Mobile App Guide",
  upsells: "Upsells",
  precheckinButton: "Pre-Checkin Button",
  stayDatesBox: "Stay Dates",
  magicLink: "Guest app",
  mobileAppLink: "Guest app",
};

function textToHtml(text) {
  if (!text) return "";
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped.replace(/\{(\w+)\}/g, (match, tag) => {
    const label = TAG_LABELS[tag] || tag;
    return (
      '<span class="tag-pill" contenteditable="false" data-tag="' +
      tag +
      '">' +
      label +
      "</span>"
    );
  });
}

function getPlainText(el) {
  let text = "";
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.classList && node.classList.contains("tag-pill")) {
        text += "{" + node.getAttribute("data-tag") + "}";
      } else {
        for (const child of node.childNodes) {
          walk(child);
        }
      }
    }
  };
  for (const child of el.childNodes) {
    walk(child);
  }
  return text;
}

export default function TagTextInput({
  value = "",
  onChange,
  onBlur,
  tags = DEFAULT_TAGS,
  placeholder,
  ...rest
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [filter, setFilter] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const editorRef = useRef(null);
  const menuRef = useRef(null);
  const braceNodeRef = useRef(null);
  const braceOffsetRef = useRef(-1);
  const isInternalChange = useRef(false);

  const filteredTags = tags.filter((t) =>
    t.toLowerCase().startsWith(filter.toLowerCase()),
  );

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    const el = editorRef.current;
    if (!el) return;
    const currentText = getPlainText(el);
    if (currentText !== value) {
      el.innerHTML = textToHtml(value);
    }
  }, [value]);

  const insertTag = useCallback(
    (tag) => {
      const el = editorRef.current;
      if (!el) return;

      const textNode = braceNodeRef.current;
      const braceOffset = braceOffsetRef.current;

      if (textNode && textNode.parentNode) {
        const fullText = textNode.textContent;
        const beforeBrace = fullText.slice(0, braceOffset);
        const afterPartial = fullText.slice(braceOffset + 1 + filter.length);

        const pill = document.createElement("span");
        pill.className = "tag-pill";
        pill.contentEditable = "false";
        pill.setAttribute("data-tag", tag);
        pill.textContent = TAG_LABELS[tag] || tag;

        const parent = textNode.parentNode;

        if (beforeBrace) {
          parent.insertBefore(document.createTextNode(beforeBrace), textNode);
        }
        parent.insertBefore(pill, textNode);

        const afterText = afterPartial || "\u200B";
        const afterNode = document.createTextNode(afterText);
        parent.insertBefore(afterNode, textNode);
        parent.removeChild(textNode);

        const sel = window.getSelection();
        const range = document.createRange();
        range.setStart(afterNode, afterPartial ? 0 : 1);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);

        isInternalChange.current = true;
        onChange(getPlainText(el));
      }

      setShowMenu(false);
      setFilter("");
    },
    [filter, onChange],
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !showMenu) {
      e.preventDefault();
      return;
    }
    if (!showMenu) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, filteredTags.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (filteredTags.length > 0) {
        e.preventDefault();
        insertTag(filteredTags[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      setShowMenu(false);
      setFilter("");
    }
  };

  const handleInput = () => {
    const el = editorRef.current;
    if (!el) return;

    // Strip block elements (keep single line)
    el.querySelectorAll("br").forEach((br) => br.remove());
    el.querySelectorAll("div").forEach((div) => {
      while (div.firstChild) div.parentNode.insertBefore(div.firstChild, div);
      div.remove();
    });

    const sel = window.getSelection();
    if (!sel.rangeCount) {
      setShowMenu(false);
      isInternalChange.current = true;
      onChange(getPlainText(el));
      return;
    }

    const range = sel.getRangeAt(0);
    const textNode = range.startContainer;

    if (textNode.nodeType !== Node.TEXT_NODE) {
      setShowMenu(false);
      setFilter("");
      isInternalChange.current = true;
      onChange(getPlainText(el));
      return;
    }

    const cursorOffset = range.startOffset;
    const textBeforeCursor = textNode.textContent.slice(0, cursorOffset);
    const lastBrace = textBeforeCursor.lastIndexOf("{");
    const lastCloseBrace = textBeforeCursor.lastIndexOf("}");

    if (lastBrace > lastCloseBrace) {
      const partial = textBeforeCursor.slice(lastBrace + 1);
      if (/^[a-zA-Z]*$/.test(partial)) {
        braceNodeRef.current = textNode;
        braceOffsetRef.current = lastBrace;
        setFilter(partial);
        setSelectedIndex(0);
        setShowMenu(true);
        isInternalChange.current = true;
        onChange(getPlainText(el));
        return;
      }
    }

    setShowMenu(false);
    setFilter("");
    isInternalChange.current = true;
    onChange(getPlainText(el));
  };

  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        e.target !== editorRef.current &&
        !editorRef.current?.contains(e.target)
      ) {
        setShowMenu(false);
        setFilter("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showMenu]);

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain").replace(/\n/g, " ");
    document.execCommand("insertText", false, text);
  };

  return (
    <Box style={{ position: "relative", flex: 1 }}>
      <style>
        {".tag-pill {" +
          "  display: inline-block;" +
          "  background: #e0f7fa;" +
          "  border: 1px solid #4dd0e1;" +
          "  border-radius: 12px;" +
          "  padding: 1px 8px;" +
          "  margin: 0 2px;" +
          "  font-size: 13px;" +
          "  color: #00838f;" +
          "  font-weight: 500;" +
          "  user-select: all;" +
          "  cursor: default;" +
          "  line-height: 1.4;" +
          "  vertical-align: baseline;" +
          "}" +
          ".tag-input-editor:empty:before {" +
          "  content: attr(data-placeholder);" +
          "  color: #999;" +
          "  pointer-events: none;" +
          "}"}
      </style>
      <div
        ref={editorRef}
        className="tag-input-editor"
        contentEditable
        data-placeholder={placeholder || ""}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={() => {
          if (onBlur)
            onBlur(editorRef.current ? getPlainText(editorRef.current) : value);
        }}
        suppressContentEditableWarning
        style={{
          padding: "8px 12px",
          border: "1px solid #cccccc",
          borderRadius: 4,
          fontSize: 14,
          fontFamily: "inherit",
          width: "100%",
          boxSizing: "border-box",
          whiteSpace: "nowrap",
          overflow: "hidden",
          outline: "none",
          lineHeight: "1.6",
          minHeight: 36,
        }}
      />
      {showMenu && filteredTags.length > 0 && (
        <Box
          ref={menuRef}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 100,
            marginTop: 2,
          }}
          background="white"
          elevation="medium"
          round="xsmall"
          border={{ color: "light-4" }}
          width={{ min: "180px" }}
          overflow="auto"
          pad={{ vertical: "xxsmall" }}
        >
          {filteredTags.map((tag, i) => (
            <Box
              key={tag}
              pad={{ horizontal: "small", vertical: "xxsmall" }}
              background={i === selectedIndex ? "light-2" : "white"}
              hoverIndicator="light-2"
              onMouseDown={(e) => {
                e.preventDefault();
                insertTag(tag);
              }}
              style={{ cursor: "pointer" }}
            >
              <Text size="small">
                <span
                  style={{
                    display: "inline-block",
                    background: "#e0f7fa",
                    border: "1px solid #4dd0e1",
                    borderRadius: 12,
                    padding: "1px 8px",
                    color: "#00838f",
                    fontWeight: 500,
                    fontSize: 13,
                  }}
                >
                  {TAG_LABELS[tag] || tag}
                </span>
              </Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
